import {ExpandLess, ExpandMore} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  CssBaseline,
  Divider,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import {useEffect, useMemo, useRef, useState} from "react";

import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {MdOutlineNotificationImportant} from "react-icons/md";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import {IconButton} from "@chakra-ui/react";
import {
  completedSetup,
  loadSetup,
  submitBankConnected,
  submitPracticeDetail,
  submitPracticePerformance,
  updateHighlevelSaasPipelineStage,
} from "../../../api/customerSetup";
import {fetchPlaiditems,} from "../../../api/plaid";
import {deriveAccountTypeKey, deriveAccountTypeKeyFromApi,} from "../../../utils/bankAccountTypeUtils";
import {fetchCurrentProfile} from "../../../api/profile";
import SetupFlowLayout from "../../welcomeSetup/elements/SetupFlowLayout";
import SetupFlowContent from "../../welcomeSetup/elements/SetupFlowContent";
import {createCustomerOnboardingId, removeCustomerOnboardingId} from "../../welcomeSetup/setupPage";
import {muiTheme} from "../../welcomeSetup/theme/theme";
import {
  buildDraftAccountSetupRows,
  loadDraftBankAccountRows,
  saveDraftBankAccountRows,
} from "../../welcomeSetup/utils";

export const getCustomerOnboardingProgress = (profile, customerId) => {
  if (!profile?.customers || !customerId) {
    return {
      completedSteps: 0,
      totalSteps: 4,
      progress: 0,
    };
  }

  const customer = profile.customers.find(
    (c) => String(c.id) === String(customerId),
  );

  if (!customer) {
    return {
      completedSteps: 0,
      totalSteps: 4,
      progress: 0,
      customer: null,
    };
  }

  const completedSteps = customer.completed_setup_steps?.length || 0;

  const totalSteps = 4;
  const progress = (completedSteps / totalSteps) * 100;

  return {
    customer,
    completedSteps,
    totalSteps,
    progress,
  };
};

export const getCustomerTaskData = (profile, customerId) => {
  if (!profile?.customers || !customerId) return [];

  const customer = profile.customers.find(
    (c) => String(c.id) === String(customerId),
  );

  if (!customer) return [];

  const completed = customer.completed_setup_steps || [];

  const isDone = (stepKey) => completed.includes(stepKey);

  return [
    {
      key: "practice_detail",
      icon: (
        <Box
          component="img"
          src="/onboarding-step1.svg"
          alt=""
          sx={{ width: 22, height: 22, objectFit: "contain" }}
        />
      ),
      title: "Set Up",
      description: "Configure your business details and preferences.",
      isComplete: isDone("practice_detail"),
    },
    {
      key: "bank_connection",
      icon: (
        <Box
          component="img"
          src="/onboarding-step2.svg"
          alt=""
          sx={{ width: 22, height: 22, objectFit: "contain" }}
        />
      ),
      title: "Link Bank Accounts",
      description:
        "Connect your primary business accounts for real-time tracking.",
      isComplete: isDone("bank_linked"),
    },
    {
      key: "performance_overview",
      icon: (
        <Box
          component="img"
          src="/onboarding-step3.svg"
          alt=""
          sx={{ width: 22, height: 22, objectFit: "contain" }}
        />
      ),
      title: "Performance Overview",
      description: "Input your initial business performance metrics.",
      isComplete: isDone("performance_overview"),
    },
    {
      key: "book_a_call",
      icon: (
        <Box
          component="img"
          src="/onboarding-step4.svg"
          alt=""
          sx={{ width: 22, height: 22, objectFit: "contain" }}
        />
      ),
      title: "Schedule Intro Call",
      description:
        "Meet with a Lucro team member to take you through your dashboard.",
      isComplete: isDone("book_a_call"),
    },
  ];
};

const SetupModal = ({
  open,
  profile,
  setProfile,
  customer_id = null,
  customerCompletedSteps = [],
  handleCloseSetup,
}) => {
  const createSetupInputData = (overrides = {}) => ({
    businessName: "",
    practiceName: "",
    businessType: "",
    practiceType: "",
    businessNiches: [],
    practiceNiches: [],
    address1: "",
    address2: "",
    city: "",
    zip: "",
    businessMode: "",
    practiceMode: "",
    acceptsInsurance: null,
    seesPatientsInPerson: null,
    peopleAtBusiness: "",
    peopleAtPractice: "",
    healthCareProviders: "",
    staff: "",
    weeklyPatients: "",
    averageWeeklyPatients: "",
    handleablePatientsWeekly: "",
    handleableNewPatientsWeekly: "",
    marketingFields: [],
    addedBusinessContext: "",
    addedPracticeContext: "",
    ...overrides,
  });

  const createSetupInputDataFromCustomer = (currentCustomer) =>
    createSetupInputData({
      businessName: currentCustomer.name || "",
      practiceName: currentCustomer.name || "",
      businessType: currentCustomer.business_type || "",
      practiceType: currentCustomer.business_type || "",
      businessNiches: currentCustomer.business_niches || [],
      practiceNiches: currentCustomer.business_niches || [],
      address1: currentCustomer.address1 || "",
      address2: currentCustomer.address2 || "",
      city: currentCustomer.city || "",
      zip: currentCustomer.zip_code || "",
      businessMode: currentCustomer.business_mode || "",
      practiceMode: currentCustomer.business_mode || "",
      acceptsInsurance: currentCustomer.accepts_insurance ?? null,
      seesPatientsInPerson: currentCustomer.sees_patients_in_person ?? null,
      peopleAtBusiness: currentCustomer.people_at_business || "",
      peopleAtPractice: currentCustomer.people_at_business || "",
      healthCareProviders: currentCustomer.health_care_providers || "",
      staff: currentCustomer.staff || "",
      weeklyPatients: currentCustomer.weekly_patients || "",
      averageWeeklyPatients: currentCustomer.average_weekly_patients || "",
      handleablePatientsWeekly:
        currentCustomer.handleable_patients_weekly || "",
      handleableNewPatientsWeekly:
        currentCustomer.handleable_new_patients_weekly || "",
      marketingFields: currentCustomer.marketing_fields || [],
      addedBusinessContext: currentCustomer.added_business_context || "",
      addedPracticeContext: currentCustomer.added_business_context || "",
    });

  const getBusinessValue = (data, businessKey, legacyKey) => {
    return data?.[businessKey] ?? data?.[legacyKey];
  };

  const toCompatibilityPayload = (data) => {
    const businessName = getBusinessValue(data, "businessName", "practiceName");
    const businessType = getBusinessValue(data, "businessType", "practiceType");
    const businessNiches =
      getBusinessValue(data, "businessNiches", "practiceNiches") || [];
    const businessMode = getBusinessValue(data, "businessMode", "practiceMode");
    const peopleAtBusiness = getBusinessValue(
      data,
      "peopleAtBusiness",
      "peopleAtPractice",
    );
    const addedBusinessContext = getBusinessValue(
      data,
      "addedBusinessContext",
      "addedPracticeContext",
    );

    return {
      ...data,
      businessName,
      practiceName: businessName,
      businessType,
      practiceType: businessType,
      businessNiches,
      practiceNiches: businessNiches,
      businessMode,
      practiceMode: businessMode,
      peopleAtBusiness,
      peopleAtPractice: peopleAtBusiness,
      addedBusinessContext,
      addedPracticeContext: addedBusinessContext,
    };
  };

  const [customer, setCustomer] = useState(() => {
    const storedId = localStorage.getItem("customerOnboardingId");
    if (!storedId || storedId === "null") return customer_id;
    return storedId;
  });

  const [mounted, setMounted] = useState(false);

  const [stepperStep, setStepperStep] = useState(0);

  const [part1Step, setPart1Step] = useState(stepperStep > 0 ? 2 : 0);
  const [part2Step, setPart2Step] = useState(0);
  const [part3Step, setPart3Step] = useState(0);
  const [prompt, setPrompt] = useState(true);

  const [bankLinked, setBankLinked] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [plaidItems, setPlaidItems] = useState([]);
  const [showConfirmAccountsDialog, setShowConfirmAccountsDialog] =
    useState(false);
  const [showProceedWithoutConnecting, setShowProceedWithoutConnecting] =
    useState(false);
  const [creatingCsvAccount, setCreatingCsvAccount] = useState(false);
  const [csvSnackbarOpen, setCsvSnackbarOpen] = useState(false);
  const [csvSnackbarMsg, setCsvSnackbarMsg] = useState("");
  const [csvSnackbarSeverity, setCsvSnackbarSeverity] = useState("error");

  useEffect(() => {
  if (stepperStep !== 3) return;
    if (!profile?.customers || !customer) return;
    const currentCustomer = profile.customers.find(
      (c) => String(c.id) === String(customer)
    );
    if (!currentCustomer) return;
    const completedSteps = currentCustomer.completed_setup_steps || [];
    if (completedSteps.includes("book_a_call")) {
      setStepperStep((prev) => prev + 1);
    }
  }, [profile, customer, stepperStep, setStepperStep]);

  const [draftAccountSetupRows, setDraftAccountSetupRows] = useState([]);
  const [plaidLinking, setPlaidLinking] = useState(false);
  const [pendingDraftConnectMethod, setPendingDraftConnectMethod] = useState(null);
  const [draftConnectRowId, setDraftConnectRowId] = useState(null);
  const draftCsvFilesByRowIdRef = useRef({});
  const draftCsvTargetRowIdRef = useRef(null);
  const draftCsvFilePickerRef = useRef(null);

  const draftConnectedCount = (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : [])
    .filter((r) => r?.isConnected).length;
  const draftAccountsAllNamed =
    (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : []).length > 0 &&
    (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : []).every((r) =>
      Boolean((r?.bankName || "").trim()) && Boolean((r?.accountName || "").trim())
    );
  const allAccountsConnected = draftConnectedCount > 0 && draftConnectedCount === draftAccountSetupRows.length;

  const seededAccountSetupFromExistingRef = useRef(false);
  const connectSnapshotRef = useRef(null);
  const draftBankAccountStorageHydratedRef = useRef(false);

  useEffect(() => {
    seededAccountSetupFromExistingRef.current = false;
  }, [customer]);

  useEffect(() => {
    draftBankAccountStorageHydratedRef.current = false;
  }, [customer]);

  const refreshPlaidItemList = async (id) => {
    if (!id) return;

    try {
      const items = await fetchPlaiditems(id);
      const isLinked = Array.isArray(items) && items.length > 0;
      setPlaidItems(Array.isArray(items) ? items : []);
      setBankLinked(isLinked);
      return items;
    } catch (err) {
      console.error("Error fetching plaid items", err);
    }
  };

  const [checkingPlaid, setCheckingPlaid] = useState(false);

  useEffect(() => {
    const checkPlaidStatus = async () => {
      if (!customer) {
        setCheckingPlaid(false);
        setBankLinked(false);
        return;
      }
      setCheckingPlaid(true);
      try {
        const plaidItems = await fetchPlaiditems(customer);
        const isLinked = Array.isArray(plaidItems) && plaidItems.length > 0;
        setPlaidItems(Array.isArray(plaidItems) ? plaidItems : []);
        setBankLinked(isLinked);
      } catch (err) {
        console.error("❌ Error fetching plaid items:", err);
      } finally {
        setCheckingPlaid(false);
      }
    };
    if (stepperStep === 1) {
      checkPlaidStatus();
    }
  }, [customer, stepperStep]);

  useEffect(() => {
    if (stepperStep === 1 && customer) {
      refreshPlaidItemList(customer);
    }
  }, [stepperStep, customer]);

  useEffect(() => {
    if (customer) {
      createCustomerOnboardingId(customer);
    } else {
      removeCustomerOnboardingId();
    }
  }, [customer]);

  const currentPartStep =
    stepperStep === 0
      ? part1Step
      : stepperStep === 1
        ? part2Step
        : stepperStep === 2
          ? part3Step
          : 0;

  const [inputData, setInputData] = useState(createSetupInputData());

  const [initialData, setInitialData] = useState(createSetupInputData());

  const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  useEffect(() => {
    if (!profile) return;

    const storedCustomerId = localStorage.getItem("customerOnboardingId");
    const customerId =
      storedCustomerId && storedCustomerId !== "null" ? storedCustomerId : null;

    if (customerId) {
      setCustomer(customerId);

      const currentCustomer = profile.customers.find(
        (c) => String(c.id) === String(customerId),
      );
      if (currentCustomer) {
        const hydratedData = createSetupInputDataFromCustomer(currentCustomer);
        setInputData(hydratedData);
        setInitialData(hydratedData);
      }

      return;
    }

    const controller = new AbortController();

    const fetchSetup = async () => {
      try {
        const data = await loadSetup({ signal: controller.signal });
        customerId = data?.customer_id ?? null;

        if (customerId) {
          setCustomer(customerId);
          createCustomerOnboardingId(customerId);

          const currentCustomer = profile.customers.find(
            (c) => c.id === customerId,
          );

          if (currentCustomer) {
            const hydratedData = createSetupInputDataFromCustomer(currentCustomer);
            setInputData(hydratedData);
            setInitialData(hydratedData);
          }
        } else {
          setCustomer(null);
          removeCustomerOnboardingId();
        }
      } catch (err) {
        console.error("❌ setup load failed:", err);
      }
    };

    if (!mounted) {
      fetchSetup();
      setMounted(true);
    }

    return () => controller.abort();
  }, [profile, mounted]);

  const [formErrors, setFormErrors] = useState({});

  const validatePart1 = () => {
    const errors = {};

    if (!getBusinessValue(inputData, "businessName", "practiceName"))
      errors.businessName = "Business Name is required";
    if (!getBusinessValue(inputData, "businessType", "practiceType"))
      errors.businessType = "Business Type is required";
    if (!(getBusinessValue(inputData, "businessNiches", "practiceNiches") || []).length)
      errors.businessNiches = "Select at least one niche";
    if (!inputData.address1) errors.address1 = "Address Line 1 is required";

    const cityRegex = /^[a-zA-Z\s'-]{2,}$/;
    if (!inputData.city) {
      errors.city = "City is required";
    } else if (!cityRegex.test(inputData.city.trim())) {
      errors.city = "City contains invalid characters";
    }

    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!inputData.zip) {
      errors.zip = "ZIP Code is required";
    } else if (!zipRegex.test(inputData.zip.trim())) {
      errors.zip = "ZIP Code format is invalid";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [formErrorsMode, setFormErrorsMode] = useState({});

  const validatePart2 = () => {
    const errors = {};
    if (!getBusinessValue(inputData, "businessMode", "practiceMode"))
      errors.businessMode = "Please select a business mode";
    setFormErrorsMode(errors);
    return Object.keys(errors).length === 0;
  };

  const [formErrorsTeam, setFormErrorsTeam] = useState({});

  const validateTeamOverview = () => {
    const errors = {};

    if (
      inputData.acceptsInsurance === null ||
      inputData.acceptsInsurance === undefined
    ) {
      errors.acceptsInsurance = "This field is required";
    }

    if (
      inputData.seesPatientsInPerson === null ||
      inputData.seesPatientsInPerson === undefined
    ) {
      errors.seesPatientsInPerson = "This field is required";
    }

    const peopleAtBusiness = getBusinessValue(
      inputData,
      "peopleAtBusiness",
      "peopleAtPractice",
    );
    if (!peopleAtBusiness) {
      errors.peopleAtBusiness = "Please enter the total number of people";
    } else if (isNaN(Number(peopleAtBusiness))) {
      errors.peopleAtBusiness = "Must be a number";
    }

    if (!inputData.healthCareProviders) {
      errors.healthCareProviders =
        "Please enter the number of healthcare providers";
    } else if (isNaN(Number(inputData.healthCareProviders))) {
      errors.healthCareProviders = "Must be a number";
    }

    if (!inputData.staff) {
      errors.staff = "Please enter the number of support/admin staff";
    } else if (isNaN(Number(inputData.staff))) {
      errors.staff = "Must be a number";
    }

    setFormErrorsTeam(errors);

    return Object.keys(errors).length === 0;
  };

  const [formErrorsWeeklyStats, setFormErrorsWeeklyStats] = useState({});

  const validateWeeklyStats = () => {
    const errors = {};

    if (!inputData.averageWeeklyPatients) {
      errors.averageWeeklyPatients =
        "Please enter the amount of average weekly patients you recieve";
    } else if (isNaN(Number(inputData.averageWeeklyPatients))) {
      errors.peopleAtBusiness = "Must be a number";
    }

    if (!inputData.weeklyPatients) {
      errors.weeklyPatients =
        "Please enter the amount of weekly patients you recieve";
    } else if (isNaN(Number(inputData.weeklyPatients))) {
      errors.weeklyPatients = "Must be a number";
    }

    if (!inputData.handleablePatientsWeekly) {
      errors.handleablePatientsWeekly =
        "Please enter the amount of weekly patients you can handle";
    } else if (isNaN(Number(inputData.handleablePatientsWeekly))) {
      errors.handleablePatientsWeekly = "Must be a number";
    }

    if (!inputData.handleableNewPatientsWeekly) {
      errors.handleableNewPatientsWeekly =
        "Please enter the amount of weekly new patients you can handle";
    } else if (isNaN(Number(inputData.handleableNewPatientsWeekly))) {
      errors.handleableNewPatientsWeekly = "Must be a number";
    }

    setFormErrorsWeeklyStats(errors);

    return Object.keys(errors).length === 0;
  };

  const [loading, setLoading] = useState(false);
  const bookACallRef = useRef(null);

  const connectedAccountRows = useMemo(
    () =>
      (Array.isArray(plaidItems) ? plaidItems : [])
        .filter((item) => item.product === "transactions")
        .flatMap((item) =>
          (item.bankaccount_set || []).map((account) => ({
            plaidItemId: item.id,
            bankName: item.name,
            csvFileName: item.import_file_name || null,
            accountId: account.id,
            accountName: account.name,
            accountType: account.type,
            accountSubtype: account.subtype,
            isCsv: Boolean(item.is_fake_import),
          }))
        ),
    [plaidItems]
  );

  const hasBankConnections =
    draftConnectedCount > 0 || connectedAccountRows.length > 0;

  useEffect(() => {
    if (stepperStep !== 1) return;
    if (!customer) return;
    if (draftBankAccountStorageHydratedRef.current) return;

    // Persist unlinked account drafts so users don't lose their work when they
    // return later to connect via CSV/Plaid.
    if (connectedAccountRows.length === 0 && draftAccountSetupRows.length === 0) {
      const restored = loadDraftBankAccountRows(customer);
      if (restored.length > 0) {
        setDraftAccountSetupRows(restored);
      }
    }

    draftBankAccountStorageHydratedRef.current = true;
  }, [
    connectedAccountRows.length,
    customer,
    draftAccountSetupRows.length,
    setDraftAccountSetupRows,
    stepperStep,
  ]);

  useEffect(() => {
    if (stepperStep !== 1) return;
    if (!customer) return;
    if (!draftBankAccountStorageHydratedRef.current) return;

    saveDraftBankAccountRows(customer, draftAccountSetupRows);
  }, [customer, draftAccountSetupRows, stepperStep]);

  useEffect(() => {
    if (stepperStep !== 1) return;

    const hasConnectedAccounts = connectedAccountRows.length > 0;

    // Seed Account Setup rows from existing connected accounts the first time,
    // so Account Setup can render immediately without showing the old table.
    if (
      hasConnectedAccounts &&
      draftAccountSetupRows.length === 0 &&
      !seededAccountSetupFromExistingRef.current
    ) {
      setDraftAccountSetupRows(
        connectedAccountRows.map((r) => ({
          id: `existing-${r.accountId}`,
          bankName: r.bankName || "",
          accountName: r.accountName || "",
          typeKey:
            deriveAccountTypeKeyFromApi({
              accountType: r.accountType,
              accountSubtype: r.accountSubtype,
            }) || "checking",
          isConnected: true,
          connectionMethod: r.isCsv ? "csv" : "plaid",
          source: "existing",
          sourceAccountId: r.accountId,
          sourcePlaidItemId: r.plaidItemId,
        }))
      );
      seededAccountSetupFromExistingRef.current = true;
    }

    // If the user deleted all accounts while on the setup grid, reset to a single default
    // checking draft row so the grid is never left empty. The "How Many Accounts" step has
    // been removed; there is no intermediate step to return to.
    if (!hasConnectedAccounts && part2Step === 3) {
      const onlySeeded =
        (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : []).length > 0 &&
        (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : []).every(
          (r) => r?.source === "existing"
        );

      if (onlySeeded) {
        setDraftAccountSetupRows(
          buildDraftAccountSetupRows({ checking: 1, savings: 0, credit: 0 })
        );
      }
    }
  }, [
    connectedAccountRows,
    draftAccountSetupRows,
    part2Step,
    setDraftAccountSetupRows,
    setPart2Step,
    stepperStep,
  ]);

  useEffect(() => {
    if (stepperStep !== 1) return;
    if (connectedAccountRows.length === 0) return;

    const byAccountId = new Map(
      connectedAccountRows
        .filter((r) => r?.accountId)
        .map((r) => [
          r.accountId,
          deriveAccountTypeKeyFromApi({
            accountType: r.accountType,
            accountSubtype: r.accountSubtype,
          }),
        ])
    );

    setDraftAccountSetupRows((prev) => {
      const rows = Array.isArray(prev) ? prev : [];
      let changed = false;
      const next = rows.map((row) => {
        const sourceAccountId = row?.sourceAccountId || null;
        if (!sourceAccountId) return row;

        const nextTypeKey = byAccountId.get(sourceAccountId) || "";
        if (!nextTypeKey) return row;
        if (row?.typeKey === nextTypeKey) return row;
        changed = true;
        return { ...row, typeKey: nextTypeKey };
      });

      return changed ? next : prev;
    });
  }, [connectedAccountRows, setDraftAccountSetupRows, stepperStep]);

  useEffect(() => {
    if (stepperStep !== 1) return;
    if (part2Step !== 2) return;

    // Legacy Create/Edit account screen (part2Step=2) is retired.
    // Always send users to the new Account Setup experience.
    setPart2Step(3);
  }, [part2Step, stepperStep]);
  /* ------------------------ RENDER ------------------------ */
  if (!open) return null;

  const steps = [
    {
      title: "Step 1: Set Up",
      icon: (
        <Box
          component="img"
          src="/Healthcare Streamline UX Duotone.svg"
          alt="Set Up"
          sx={{
            width: { xs: 70, sm: 85, md: 99 },
            height: { xs: 70, sm: 85, md: 99 },
            objectFit: "contain",
          }}
        />
      ),
      time: "(Approx. 2mins)",
    },
    {
      title: "Step 2: Link Bank Account",
      icon: (
        <Box
          component="img"
          src="/Link Hand 1 Streamline UX Colors.svg"
          alt="Link Bank Account"
          sx={{
            width: { xs: 70, sm: 85, md: 99 },
            height: { xs: 70, sm: 85, md: 99 },
            objectFit: "contain",
          }}
        />
      ),
      time: "(Approx. 5mins)",
    },
    {
      title: "Step 3: Performance Overview",
      icon: (
        <Box
          component="img"
          src="/Browser Window Check Streamline UX Duotone.svg"
          alt="Performance Overview"
          sx={{
            width: { xs: 70, sm: 85, md: 99 },
            height: { xs: 70, sm: 85, md: 99 },
            objectFit: "contain",
          }}
        />
      ),
      time: "(Approx. 3mins)",
    },
    {
      title: "Step 4: Schedule Intro Call",
      icon: (
        <Box
          component="img"
          src="/book-a-call.svg"
          alt="Schedule Intro Call"
          sx={{
            width: { xs: 70, sm: 85, md: 99 },
            height: { xs: 70, sm: 85, md: 99 },
            objectFit: "contain",
          }}
        />
      ),
      time: "(Approx. 2mins)",
    },
  ];

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {showConfirmAccountsDialog && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2100, // high enough to be above everything
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "rgba(0,0,0,0.55)",
            overflowY: "auto",
            p: 3,
          }}
        >
          <Card
            sx={{
              width: { xs: "100%", sm: "640px" },
              borderRadius: "16px",
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                backgroundColor: "#fff",
                border: "1px solid rgba(33,33,33,0.08)",
                borderRadius: "16px",
                boxShadow: "0px 2px 4px 0px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  px: "24px",
                  py: "16px",
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Box sx={{ pr: "6px" }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "rgba(83,109,236,0.10)",
                    }}
                  >
                    <Box
                      sx={{
                        color: "#536DEC",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "& svg": { width: 24, height: 24 },
                      }}
                    >
                      <MdOutlineNotificationImportant />
                    </Box>
                  </Avatar>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans",
                      fontWeight: 600,
                      fontSize: "20px",
                      lineHeight: "160%",
                      letterSpacing: "0.15px",
                      color: "rgba(0,0,0,0.87)",
                    }}
                  >
                    Confirm Accounts
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "143%",
                      letterSpacing: "0.17px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Review your connected accounts to confirm nothing is
                    missing.
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                  py: "24px",
                }}
              >
                <Box sx={{ px: "24px" }}>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "150%",
                      letterSpacing: "0.098px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    You have connected{" "}
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "Rethink Sans",
                        fontWeight: 600,
                        color: "rgba(0,0,0,0.87)",
                      }}
                    >
                      {connectedAccountRows.length} accounts
                    </Box>
                    . To ensure your dashboard provides accurate financial
                    insights, please verify that these are all the accounts you
                    use for your practice.
                  </Typography>
                </Box>

                <Box sx={{ px: "24px" }}>
                  <Box
                    sx={{
                      backgroundColor: "#F4F4F6",
                      borderRadius: "8px",
                      p: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {connectedAccountRows.length === 0 && (
                      <Typography
                        sx={{
                          fontFamily: "Rethink Sans",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "150%",
                          letterSpacing: "0.098px",
                          color: "rgba(0,0,0,0.6)",
                          textAlign: "center",
                          py: "12px",
                        }}
                      >
                        0 connected accounts
                      </Typography>
                    )}
                    {connectedAccountRows.map((row) => (
                      <Box
                        key={`${row.plaidItemId}-${row.accountId}`}
                        sx={{
                          backgroundColor: "#fff",
                          border: "1px solid #E0E0E0",
                          borderRadius: "4px",
                          px: "13px",
                          py: "10px",
                          boxShadow: "0px 0px 8px 0px rgba(0,0,0,0.08)",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "999px",
                            backgroundColor: "rgba(83,109,236,0.10)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AccountBalanceOutlinedIcon
                            sx={{ color: "#536DEC", fontSize: 16 }}
                          />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={row.bankName || ""} placement="top" disableHoverListener={!row.bankName}>
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans",
                                fontWeight: 600,
                                fontSize: "14px",
                                lineHeight: "157%",
                                letterSpacing: "0.1px",
                                color: "#101828",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.bankName}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={row.accountName || ""} placement="top" disableHoverListener={!row.accountName}>
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans",
                                fontWeight: 400,
                                fontSize: "12px",
                                lineHeight: "143%",
                                letterSpacing: "0.17px",
                                color: "#6A7282",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.accountName}
                            </Typography>
                          </Tooltip>
                        </Box>

                        <Box
                          sx={{
                            maxHeight: "24px",
                            minHeight: "24px",
                            borderRadius: "100px",
                            backgroundColor: row.isCsv
                              ? "rgba(2,136,209,0.08)"
                              : "rgba(46,125,50,0.08)",
                            px: "14px",
                            py: "3px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Rethink Sans",
                              fontWeight: 500,
                              fontSize: "13px",
                              lineHeight: "18px",
                              letterSpacing: "0.16px",
                              color: row.isCsv ? "#0288D1" : "#2E7D32",
                            }}
                          >
                            {row.isCsv ? "CSV" : "Linked"}
                          </Typography>
                          {row.isCsv ? (
                            <DescriptionOutlinedIcon
                              sx={{ fontSize: 16, color: "#0288D1" }}
                            />
                          ) : (
                            <LinkOutlinedIcon
                              sx={{ fontSize: 16, color: "#2E7D32" }}
                            />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  px: "20px",
                  py: "16px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "16px",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowConfirmAccountsDialog(false);
                    setPart2Step(3);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    borderColor: "rgba(83,109,236,0.5)",
                    color: "#536DEC",
                    px: "16px",
                    py: "6px",
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setShowConfirmAccountsDialog(false);

                    if (hasBankConnections) {
                      localStorage.removeItem("skipped_bank_setup");
                    } else {
                      localStorage.setItem("skipped_bank_setup", "true");
                    }

                    if (!allAccountsConnected) {
                      localStorage.setItem("partial_bank_linking", "true");
                    } else {
                      localStorage.removeItem("partial_bank_linking");
                    }

                    try {
                        if (customer && hasBankConnections) {
                          await submitBankConnected(inputData, customer);
                        }
                        const [currentProfile] =
                          await fetchCurrentProfile();
                        setProfile(currentProfile);
                    } catch (err) {
                      console.error("Failed confirming bank step:", err);
                    }
                    updateHighlevelSaasPipelineStage(
                      allAccountsConnected
                        ? "Schedule Call"
                        : "Complete Partial Bank",
                    );
                    setPart3Step(0);
                    setStepperStep(2);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    backgroundColor: muiTheme.palette.primary.main,
                    "&:hover": { backgroundColor: "#4C63D7" },
                    px: "16px",
                    py: "6px",
                    boxShadow:
                      "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
                  }}
                >
                  Yes, Continue
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>
      )}

      {prompt === true && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.55)",
            overflowY: "auto",
            pt: 6,
            pb: 6,
          }}
        >
          <Box
            minHeight={"80vh"}
            sx={{
              minHeight: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              px: { xs: 2, sm: 4 },
            }}
          >
            <Paper
              elevation={1}
              sx={{
                maxWidth: "1100px",
                borderRadius: "16px",
                border: "1px solid #21212114",
                boxShadow: "0px 2px 4px 0px #00000013",
                p: { xs: 2.5, sm: 4, md: 6 },
                mx: "auto",

                px: { xs: 2, sm: 4, md: 6 },
                width: {
                  xs: "calc(100% - 50px)",
                  sm: "calc(100% - 50px)",
                  md: "100%",
                },
              }}
            >
              {/* Header */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  fontFamily="Rethink Sans"
                  fontWeight={600}
                  fontSize={{ xs: "22px", sm: "26px", md: "28px" }}
                  lineHeight="117%"
                  color="#000000DE"
                >
                  {`Welcome to Lucro, ${profile?.user?.first_name || ""}!`}
                </Typography>

                <Typography
                  mt={1.5}
                  fontFamily="Rethink Sans"
                  fontSize={{ xs: "14px", sm: "15px", md: "16px" }}
                  lineHeight="150%"
                  letterSpacing="0.15px"
                  color="#00000099"
                >
                  Let’s get your account set up so you can start growing your
                  business with clarity and confidence.
                </Typography>
              </Box>

              {/* Steps */}
              <Box
                sx={{
                  mt: "40px",
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                {steps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      minWidth: 0,
                      minHeight: { xs: "187px", md: "220px" },
                    }}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        height: "100%",
                        minHeight: "187px",
                        minWidth: 0,
                        flex: 1,
                        borderRadius: "6.66px",
                        borderWidth: "1px",
                        border: "1px solid #12162114",
                        boxShadow: "0px 0px 2.61px 0px #0000001F",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <CardContent
                        sx={{ p: 2, width: "100%", "&:last-child": { pb: 2 } }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap={1}
                        >
                          <Typography
                            fontFamily="Rethink Sans"
                            fontWeight="600"
                            fontSize={{ xs: "14px", sm: "16px" }}
                            lineHeight="133%"
                            letterSpacing="0px"
                            textAlign="center"
                            sx={{
                              color: "#000000DE",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {step.title}
                          </Typography>

                          {step.icon}

                          <Typography
                            fontFamily="Rethink Sans"
                            fontSize="13px"
                            lineHeight="150%"
                            letterSpacing="0.1px"
                            textAlign="center"
                            sx={{ color: "#00000099" }}
                          >
                            {step.time}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>

              {/* Bottom Buttons */}
              <Box
                sx={{
                  display: "flex",
                  width: "100%", // important
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: { xs: "center", sm: "flex-end" }, // push right on desktop
                  alignItems: "center",
                  mt: { xs: 3, sm: 4 },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    sx={{
                      color: muiTheme.palette.primary.main,
                      textTransform: "none",
                      fontWeight: 600,
                      alignSelf: { xs: "center", sm: "flex-start" },
                    }}
                    onClick={() => handleCloseSetup()}
                  >
                    Return To Demo
                  </Button>

                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      width: { xs: "100%", sm: "auto" },
                      backgroundColor: muiTheme.palette.primary.main,
                      "&:hover": { backgroundColor: "#4C63D7" },
                      boxShadow: "0px 6px 8px -2px #00000033",
                    }}
                    onClick={async () => {
                      const completed = Array.isArray(customerCompletedSteps)
                        ? customerCompletedSteps
                        : [];
                      let hasConnectedAccounts = false;

                      if (customer) {
                        try {
                          const items = await refreshPlaidItemList(customer);
                          hasConnectedAccounts =
                            Array.isArray(items) && items.length > 0;
                        } catch (err) {
                          hasConnectedAccounts = false;
                        }
                      }

                      let nextStep = 0;
                      if (!completed.includes("practice_detail")) {
                        nextStep = 0;
                      } else if (!hasConnectedAccounts) {
                        nextStep = 1;
                      } else if (!completed.includes("performance_overview")) {
                        nextStep = 2;
                      } else if (!completed.includes("book_a_call")) {
                        nextStep = 3;
                      } else {
                        nextStep = 4;
                      }

                      setStepperStep(nextStep);
                      setPrompt(false);
                    }}
                  >
                    Proceed With Set Up
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}
      {prompt === false && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.55)",
            overflowY: "auto",
            pt: 6,
            pb: 6,
          }}
        >
          <SetupFlowLayout
            stepperStep={stepperStep}
            part2Step={part2Step}
            currentPartStep={currentPartStep}
            headerExtra={
              stepperStep === 1 && part2Step === 2 ? (
                <IconButton
                  onClick={() => setPart2Step(0)}
                  sx={{ p: "5px" }}
                >
                  <CloseIcon />
                </IconButton>
              ) : null
            }
            footer={
              stepperStep !== 4 ? (
                <Box
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    mt: "auto",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent:
                      stepperStep === 1 && part2Step === 0
                        ? "space-between"
                        : stepperStep === 1
                          ? "flex-end"
                          : "space-between",
                    alignItems: "center",
                    p: 2,
                    gap: 2,
                    height: { xs: "auto", sm: "64px" },
                    flexShrink: 0,
                    width: "100%",
                  }}
                >
                  {(stepperStep !== 1 || part2Step === 0) && (
                    <Box
                      sx={{
                        display: { xs: "none", sm: "block" },
                        width: "auto",
                      }}
                    >
                      {stepperStep > 0 ? (
                        <Button
                          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                          sx={{
                            color: muiTheme.palette.primary.main,
                            textTransform: "none",
                            fontWeight: 500,
                          }}
                          onClick={() => {
                            if (stepperStep === 1) {
                              setStepperStep(stepperStep - 1);
                            } else if (stepperStep === 2) {
                              setPart3Step(0);
                              setStepperStep(stepperStep - 1);
                            } else if (stepperStep === 3) {
                              setStepperStep(2);
                            } else {
                              setPrompt(true);
                            }
                          }}
                        >
                          {stepperStep === 1
                            ? "Return To Set Up"
                            : stepperStep === 2
                              ? "Return To Bank Connection"
                              : "Return To Performance Overview"}
                        </Button>
                      ) : (
                        <Box sx={{ width: "180px" }} />
                      )}
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column-reverse", sm: "row" },
                      gap: 2,
                      width: { xs: "100%", sm: "auto" },
                      mt: { xs: 1, sm: 0 },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    {stepperStep > 0 &&
                      (stepperStep !== 1 || part2Step === 0) && (
                        <Button
                          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                          sx={{
                            display: { xs: "flex", sm: "none" },
                            textTransform: "none",
                            fontWeight: 500,
                            width: "100%",
                            justifyContent: "flex-start",
                          }}
                          onClick={() => {
                            if (stepperStep === 1) {
                              setStepperStep(stepperStep - 1);
                            } else if (stepperStep === 2) {
                              setPart3Step(0);
                              setStepperStep(stepperStep - 1);
                            } else if (stepperStep === 3) {
                              setStepperStep(2);
                            } else {
                              setPrompt(true);
                            }
                          }}
                        >
                          {stepperStep === 1
                            ? "Return To Set Up"
                            : stepperStep === 2
                              ? "Return To Bank Connection"
                              : "Return To Performance Overview"}
                        </Button>
                      )}

                    {stepperStep !== 1 &&
                      !(stepperStep === 1 && bankLinked) &&
                      stepperStep !== 3 && (
                        <Button
                          variant="outlined"
                          sx={{
                            textTransform: "none",
                            borderRadius: "4px",
                            width: { xs: "100%", sm: "auto" },
                          }}
                          onClick={async () => {
                            if (stepperStep === 3) {
                              setStepperStep((prev) => prev + 1);
                              return;
                            }

                            if (part1Step < 1 && stepperStep === 0) {
                              setPrompt(true);
                              return;
                            }

                            if (part2Step < 1 && stepperStep === 1) {
                              setStepperStep((prev) => prev + 1);
                              return;
                            }

                            if (part3Step < 1 && stepperStep === 2) {
                              try {
                                await completedSetup();
                                const [currentProfile] =
                                  await fetchCurrentProfile();
                                setProfile(currentProfile);
                                setStepperStep((prev) => prev + 1);
                              } catch (err) {
                                console.error(
                                  "Failed to complete performance setup skip:",
                                  err,
                                );
                              }

                              return;
                            }

                            if (part1Step > 0 && stepperStep === 0) {
                              setPart1Step((prev) => prev - 1);
                            } else if (part2Step > 0 && stepperStep === 1) {
                              setPart2Step(0);
                            } else if (part3Step > 0 && stepperStep === 2) {
                              setPart3Step((prev) => prev - 1);
                            }
                          }}
                        >
                          {part1Step < 1 && stepperStep === 0
                            ? "Back"
                            : part2Step < 1 && stepperStep === 1
                              ? "Skip For Now"
                              : part3Step < 1 && stepperStep === 2
                                ? "Skip For Now"
                                : "Back"}
                        </Button>
                      )}

                    {stepperStep === 1 ? (
                      part2Step === 0 ? (
                        <>
                          <Button
                            variant="outlined"
                            sx={{
                              textTransform: "none",
                              borderRadius: "4px",
                              width: { xs: "100%", sm: "auto" },
                              borderColor: "rgba(83,109,236,0.5)",
                              color: "#536DEC",
                            }}
                            onClick={() => {
                              localStorage.setItem("skipped_bank_setup", "true");
                              setStepperStep(2);
                            }}
                          >
                            Skip For Now
                          </Button>
                          <Button
                            variant="contained"
                            sx={{
                              textTransform: "none",
                              borderRadius: "4px",
                              backgroundColor: muiTheme.palette.primary.main,
                              "&:hover": { backgroundColor: "#4C63D7" },
                              width: { xs: "100%", sm: "auto" },
                            }}
                            onClick={() => {
                              localStorage.removeItem("skipped_bank_setup");
                              if (connectedAccountRows.length > 0) {
                                if (
                                  draftAccountSetupRows.length === 0 &&
                                  !seededAccountSetupFromExistingRef.current
                                ) {
                                  setDraftAccountSetupRows(
                                    connectedAccountRows.map((r) => ({
                                      id: `existing-${r.accountId}`,
                                      bankName: r.bankName || "",
                                      accountName: r.accountName || "",
                                      typeKey: deriveAccountTypeKey(r.accountName),
                                      isConnected: true,
                                      connectionMethod: r.isCsv ? "csv" : "plaid",
                                      source: "existing",
                                      sourceAccountId: r.accountId,
                                      sourcePlaidItemId: r.plaidItemId,
                                    })),
                                  );
                                  seededAccountSetupFromExistingRef.current = true;
                                }
                                setPart2Step(3);
                                return;
                              }
                              if (draftAccountSetupRows.length === 0) {
                                setDraftAccountSetupRows(
                                  buildDraftAccountSetupRows({
                                    checking: 1,
                                    savings: 0,
                                    credit: 0,
                                  }),
                                );
                              }
                              setPart2Step(3);
                            }}
                          >
                            Link Bank Accounts
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outlined"
                            sx={{
                              textTransform: "none",
                              borderRadius: "4px",
                              width: { xs: "100%", sm: "auto" },
                              borderColor: "rgba(83,109,236,0.5)",
                              color: "#536DEC",
                            }}
                            onClick={() => setPart2Step(0)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            sx={{
                              textTransform: "none",
                              borderRadius: "4px",
                              backgroundColor: draftAccountsAllNamed
                                ? muiTheme.palette.primary.main
                                : "rgba(0,0,0,0.12)",
                              color: draftAccountsAllNamed
                                ? muiTheme.palette.primary.contrastText
                                : "rgba(0,0,0,0.38)",
                              boxShadow: draftAccountsAllNamed
                                ? "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)"
                                : "none",
                              "&:hover": {
                                backgroundColor: draftAccountsAllNamed
                                  ? "#4C63D7"
                                  : "rgba(0,0,0,0.12)",
                              },
                              width: { xs: "100%", sm: "auto" },
                            }}
                            disabled={
                              !draftAccountsAllNamed || creatingCsvAccount
                            }
                            onClick={() => {
                              if (!draftAccountsAllNamed) return;
                              if (draftConnectedCount === 0) {
                                setShowProceedWithoutConnecting(true);
                                return;
                              }
                              setShowConfirmAccountsDialog(true);
                            }}
                          >
                            Create
                          </Button>
                        </>
                      )
                    ) : (
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: "none",
                          borderRadius: "4px",
                          backgroundColor: muiTheme.palette.primary.main,
                          "&:hover": { backgroundColor: "#4C63D7" },
                          width: { xs: "100%", sm: "auto" },
                        }}
                        disabled={loading}
                        onClick={async () => {
                          setLoading(true);
                          try {
                            if (stepperStep === 0) {
                              if (part1Step === 0 && !validatePart1()) return;
                              if (part1Step === 1 && !validatePart2()) return;

                              if (part1Step < 2) {
                                setPart1Step((prev) => prev + 1);
                              } else {
                                if (!deepEqual(inputData, initialData)) {
                                  const compatibilityPayload =
                                    toCompatibilityPayload(inputData);
                                  const response = await submitPracticeDetail(
                                    compatibilityPayload,
                                    customer,
                                  );
                                  setCustomer(response.data.data.id);

                                  const [currentProfile] =
                                    await fetchCurrentProfile();
                                  setProfile(currentProfile);
                                }

                                setStepperStep((prev) => prev + 1);
                              }
                            } else if (stepperStep === 2) {
                              if (part3Step === 0 && !validateTeamOverview())
                                return;
                              if (part3Step === 1 && !validateWeeklyStats())
                                return;

                              if (part3Step < 3) {
                                setPart3Step((prev) => prev + 1);
                              } else {
                                if (!deepEqual(inputData, initialData)) {
                                  const compatibilityPayload =
                                    toCompatibilityPayload(inputData);
                                  await submitPracticePerformance(
                                    compatibilityPayload,
                                    customer,
                                  );
                                  await completedSetup();
                                  const [currentProfile] =
                                    await fetchCurrentProfile();
                                  setProfile(currentProfile);
                                }

                                setStepperStep((prev) => prev + 1);
                              }
                            } else if (stepperStep === 3) {
                              await bookACallRef.current?.submitBooking?.();
                              const [currentProfile] =
                                await fetchCurrentProfile();
                              setProfile(currentProfile);
                              setStepperStep((prev) => prev + 1);
                            }
                          } catch (err) {
                            console.error(err);
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        {loading
                          ? stepperStep === 3
                            ? "Scheduling..."
                            : "Loading..."
                          : stepperStep === 3
                            ? "Schedule"
                            : "Continue"}
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : null
            }
          >
              <SetupFlowContent
                flow={{
                  stepperStep,
                  part1Step,
                  part2Step,
                  part3Step,
                  inputData,
                  setInputData,
                  formErrors,
                  setFormErrors,
                  formErrorsMode,
                  setFormErrorsMode,
                  formErrorsTeam,
                  setFormErrorsTeam,
                  formErrorsWeeklyStats,
                  setFormErrorsWeeklyStats,
                  customer,
                  draftAccountSetupRows,
                  setDraftAccountSetupRows,
                  draftConnectRowId,
                  setDraftConnectRowId,
                  pendingDraftConnectMethod,
                  setPendingDraftConnectMethod,
                  plaidItems,
                  checkingPlaid,
                  plaidLinking,
                  showSuccess,
                  setShowSuccess,
                  creatingCsvAccount,
                  setCsvSnackbarMsg,
                  setCsvSnackbarSeverity,
                  setCsvSnackbarOpen,
                  setPart2Step,
                  refreshPlaidItemList,
                  setPlaidItems,
                  setCreatingCsvAccount,
                  setPlaidLinking,
                  setStepperStep,
                }}
                refs={{
                  bookACallRef,
                  connectSnapshotRef,
                  draftCsvFilePickerRef,
                  draftCsvTargetRowIdRef,
                  draftCsvFilesByRowIdRef,
                }}
                profile={profile}
                onProceedToDemo={async () => {
                  try {
                    const onboardingCustomerId = localStorage.getItem("customerOnboardingId");
                    if (onboardingCustomerId && onboardingCustomerId !== "null") {
                      sessionStorage.setItem("selectedCustomerId", onboardingCustomerId);
                      window.dispatchEvent(new Event("selectedCustomerId-changed"));
                    }
                    removeCustomerOnboardingId();
                    await completedSetup();
                    handleCloseSetup();
                  } catch (err) {
                    console.error(err);
                  }
                }}
              />
          </SetupFlowLayout>
          {showProceedWithoutConnecting && (
            <Box
              sx={{
                position: "fixed",
                inset: 0,
                zIndex: 2100,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "rgba(0,0,0,0.55)",
                overflowY: "auto",
                pt: 6,
                pb: 6,
              }}
            >
              <Card
                sx={{
                  borderRadius: "16px",
                  overflow: "visible",
                  maxWidth: "560px",
                  width: "calc(100% - 48px)",
                  boxShadow: "0px 2px 4px 0px rgba(0,0,0,0.08)",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  px: 3,
                  pt: 7.5, // matches DialogContent pt: 60px
                  pb: 3,
                  gap: 4, // matches DialogContent gap: 32px
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "-40px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "80px",
                    height: "80px",
                    borderRadius: "100px",
                    backgroundColor: "#EF6C00",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ErrorOutlineIcon sx={{ color: "#FFFFFF", fontSize: 40 }} />
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans",
                      fontWeight: 600,
                      fontSize: "24px",
                      lineHeight: "133%",
                      color: "rgba(0,0,0,0.87)",
                      mb: 2,
                    }}
                  >
                    Are you sure you want to proceed without connecting?
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "150%",
                      letterSpacing: "0.15px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Connecting your bank account enables accurate financial
                    tracking and unlocks the full value of Lucro. Without a live
                    connection or CSV upload, some features and insights won’t
                    be available.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      setShowProceedWithoutConnecting(false);
                    }}

                    sx={{
                      width: "100%",
                      borderRadius: "8px",
                      px: "22px",
                      py: "8px",
                      textTransform: "none",
                      fontFamily: "Rethink Sans",
                      fontWeight: 500,
                      fontSize: "15px",
                      letterSpacing: "0.46px",
                      boxShadow:
                        "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
                    }}
                  >
                    Return And Connect
                  </Button>
                  <Button
                    variant="outlined"

                    onClick={() => {
                      setShowProceedWithoutConnecting(false);

                      if (part2Step === 3) {
                        setShowConfirmAccountsDialog(true);
                        return;
                      }

                    }}
                    sx={{
                      width: "100%",
                      borderRadius: "8px",
                      px: "22px",
                      py: "8px",
                      textTransform: "none",
                      fontFamily: "Rethink Sans",
                      fontWeight: 500,
                      fontSize: "15px",
                      letterSpacing: "0.46px",
                      borderColor: "rgba(0,0,0,0.87)",
                      color: "rgba(0,0,0,0.87)",
                    }}
                  >
                    Proceed Without Connecting
                  </Button>
                </Box>
              </Card>
            </Box>
          )}

          <Snackbar
            open={csvSnackbarOpen}
            autoHideDuration={5000}
            onClose={() => setCsvSnackbarOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setCsvSnackbarOpen(false)}
              severity={csvSnackbarSeverity}
              sx={{ width: "100%" }}
            >
              {csvSnackbarMsg}
            </Alert>
          </Snackbar>
        </Box>
      )}
    </ThemeProvider>
  );
};

const OnboardingTaskCard = ({ task }) => {
  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        border: "1px solid var(--elevation-outlined, #E0E0E0)",
        borderRadius: "6px",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: "10px",
          paddingRight: "16px",
          paddingBottom: "10px !important",
          paddingLeft: "16px",
        }}
      >
        <Box
          sx={{
            width: "36px",
            height: "36px",
            bgcolor: "#536DEC1F",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          {task.icon}
        </Box>

        <Stack sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {task.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#8B8B8B", fontSize: "12px" }}
          >
            {task.description}
          </Typography>
        </Stack>

        <Chip
          label={task.isComplete ? "Complete" : "Incomplete"}
          sx={{
            bgcolor: task.isComplete ? "#DFF6E0" : "#FFD6D6",
            color: task.isComplete ? "#27AE60" : "#EB5757",
            fontWeight: 600,
            fontSize: "0.75rem",
            p: "4px 8px",
          }}
        />
      </CardContent>
    </Card>
  );
};

const OnboardingBanner = ({ customer_id, profile, setProfile }) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpand = () => setExpanded(!expanded);

  const [modalOpen, setModalOpen] = useState(false);
  const handleOpenSetup = () => setModalOpen(true);
  const handleCloseSetup = () => setModalOpen(false);

  const {
    completedSteps: computedCompletedSteps,
    totalSteps: computedTotalSteps,
    progress,
  } = useMemo(
    () => getCustomerOnboardingProgress(profile, customer_id),
    [profile, customer_id],
  );

  const taskData = useMemo(
    () => getCustomerTaskData(profile, customer_id),
    [profile, customer_id],
  );

  const customerCompletedSteps = useMemo(() => {
    if (!profile?.customers || !customer_id) return [];

    const customer = profile.customers.find(
      (c) => String(c.id) === String(customer_id),
    );

    if (!customer) return [];

    return customer.completed_setup_steps || [];
  }, [profile, customer_id]);

  const isComplete = computedCompletedSteps === computedTotalSteps;

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#536DEC0D",
        color: "#fff",
        borderRadius: "8px",
        border: "1px solid var(--primary-_states-outlinedBorder, #536DEC80)",
        overflow: "hidden",
      }}
    >
      {modalOpen && (
        <SetupModal
          open={modalOpen}
          profile={profile}
          setProfile={setProfile}
          customer_id={customer_id}
          customerCompletedSteps={customerCompletedSteps}
          handleCloseSetup={handleCloseSetup}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          p: "14px 16px",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label="DEMO MODE"
            sx={{
              px: 1,
              py: 0.3,
              bgcolor: "#536DEC1F",
              color: "#536DEC",
              borderRadius: "100px",
              fontSize: "0.8125rem",
              fontWeight: 600,
              border:
                "1px solid var(--primary-_states-outlinedBorder, #536DEC80)",
            }}
          />
          {!isComplete && (
            <Box
              sx={{
                width: "1px",
                height: "20px",
                bgcolor: "#00000014",
                display: { xs: "none", sm: "block" },
              }}
            />
          )}

          <Typography
            sx={{
              fontsize: isComplete ? "16px !important" : "14px",
              color: "#000000",
              fontWeight: 600,
            }}
            variant="body2"
          >
            <Box
              component="span"
              sx={{
                fontSize: isComplete ? "16px" : "14px",
                color: "#000000",
                fontWeight: 600,
              }}
            >
              {isComplete
                ? "Data Processing in Progress"
                : "Complete your onboarding"}
            </Box>

            {!isComplete && (
              <Box
                component="span"
                sx={{ color: "#8B8B8B", fontWeight: 400, ml: 0.5 }}
              >
                – Unlock full access to your dashboard
              </Box>
            )}
          </Typography>
        </Stack>

        {!isComplete && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "center", sm: "center" }}
            sx={{
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "center", sm: "flex-end" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="caption" sx={{ color: "#00000099" }}>
              {`${computedCompletedSteps}/${computedTotalSteps} Completed`}
            </Typography>

            <Box sx={{ width: { xs: "100%", sm: 200 } }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: "8px",
                  borderRadius: "100px",
                  bgcolor: "#E8E8EA",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: "#536DEC",
                    borderRadius: "100px",
                  },
                }}
              />
            </Box>

            <Button
              variant="contained"
              onClick={handleOpenSetup}
              sx={{
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                minWidth: { xs: "100%", sm: 120 },
                borderRadius: "6px",
                boxShadow:
                  "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)",
                backgroundColor: "#536DEC",
                "&:hover": {
                  backgroundColor: "#4C63D7",
                },
              }}
            >
              Complete Step
            </Button>

            <Box
              onClick={toggleExpand}
              sx={{
                cursor: "pointer",
                alignSelf: { xs: "flex-end", sm: "center" },
              }}
            >
              {expanded ? (
                <ExpandLess sx={{ fontSize: 30, color: "#0000008F" }} />
              ) : (
                <ExpandMore sx={{ fontSize: 30, color: "#0000008F" }} />
              )}
            </Box>
          </Stack>
        )}
      </Box>

      {!isComplete && (
        <>
          <Collapse in={expanded} timeout={300}>
            <Divider sx={{ borderColor: "#536DEC80" }} />
          </Collapse>

          <Collapse in={expanded} timeout={300}>
            <Stack spacing={2} sx={{ p: "14px 16px" }}>
              {taskData.map((task, i) => (
                <OnboardingTaskCard key={i} task={task} />
              ))}
            </Stack>
          </Collapse>
        </>
      )}
      {isComplete && (
        <Box sx={{ px: "16px", pb: "16px" }}>
          <Typography
            variant="body2"
            sx={{ color: "#00000099", lineHeight: 1.6 }}
          >
            You are currently viewing a demo account while our team processes your submitted data.
            We will present and discuss your financial data with you during your scheduled strategy call.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OnboardingBanner;
